// v2.12.1 — Mini-map preview pour la modale AddressFormModal.
// Tente Mapbox 3D (pitch 30° pour rester compact) → fallback Leaflet si token absent/invalide.
// Marker rouge auto-update sur changement lat/lng (debounce géré côté parent via les inputs).

import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
function isValidMapboxToken(t) {
  return typeof t === "string" && /^pk\./.test(t) && t.length > 20;
}

function LeafletClickPin({ lat, lng, onSet }) {
  useMapEvents({ click(e) { onSet(e.latlng.lat, e.latlng.lng); } });
  return lat != null && lng != null ? <Marker position={[lat, lng]} /> : null;
}

export default function MiniMapPreview({ lat, lng, onSet, height = 220 }) {
  const useMapbox = isValidMapboxToken(MAPBOX_TOKEN);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const center = hasCoords ? [lat, lng] : [5.348, -4.026]; // Abidjan default
  const fallbackToLeaflet = tokenInvalid || !useMapbox;

  // ─── MAPBOX init ───
  useEffect(() => {
    if (fallbackToLeaflet) return;
    if (!containerRef.current) return;
    if (mapRef.current) return; // déjà initialisé

    mapboxgl.accessToken = MAPBOX_TOKEN;
    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: "mapbox://styles/mapbox/standard",
        center: hasCoords ? [lng, lat] : [-4.026, 5.348],
        zoom: hasCoords ? 16 : 12,
        pitch: 30,
        bearing: 0,
        antialias: true,
        attributionControl: false,
      });
    } catch (err) {
      setTokenInvalid(true);
      return;
    }
    mapRef.current = map;

    map.on("error", (e) => {
      const status = e?.error?.status;
      if (status === 401 || status === 403) setTokenInvalid(true);
    });

    map.on("click", (e) => {
      onSet(e.lngLat.lat, e.lngLat.lng);
    });

    return () => {
      try { map && map.remove(); } catch (_) {}
      mapRef.current = null;
      markerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackToLeaflet]);

  // Update marker + recenter quand lat/lng changent.
  useEffect(() => {
    if (fallbackToLeaflet) return;
    const map = mapRef.current;
    if (!map) return;
    if (!hasCoords) {
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
      return;
    }
    if (markerRef.current) {
      markerRef.current.setLngLat([lng, lat]);
    } else {
      markerRef.current = new mapboxgl.Marker({ color: "#DC2626" })
        .setLngLat([lng, lat])
        .addTo(map);
    }
    map.easeTo({ center: [lng, lat], zoom: 16, duration: 400 });
  }, [lat, lng, hasCoords, fallbackToLeaflet]);

  if (fallbackToLeaflet) {
    return (
      <div data-testid="addr-form-map" data-engine="leaflet" style={{ height, borderRadius: 8, overflow: "hidden" }}>
        <MapContainer center={center} zoom={hasCoords ? 16 : 12} style={{ width: "100%", height: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
          <LeafletClickPin lat={hasCoords ? lat : null} lng={hasCoords ? lng : null} onSet={onSet} />
        </MapContainer>
      </div>
    );
  }

  return (
    <div data-testid="addr-form-map" data-engine="mapbox" style={{ height, borderRadius: 8, overflow: "hidden", position: "relative" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}
